'use client';

/**
 * Magazine-cover-style hero — large display headline with optional
 * top/bottom rules. Ported from ThemeMaker's
 * {@code blocks/hero/HeroEditorial}.
 *
 * <h3>Size</h3>
 * <ul>
 *   <li>{@code display} (default) — magazine-cover headline
 *       {@code clamp(40px, 7vw, 96px)}; generous padding; compact
 *       pill CTA. Use as THE marketing headline of the page.</li>
 *   <li>{@code compact} — feed-header treatment
 *       {@code clamp(28px, 3.6vw, 40px)}; half vertical padding; no
 *       rule lines by default. Use when the hero sits above a feed
 *       (Welcome back / Discover what's playing).</li>
 * </ul>
 */
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { tx } from '../_shared/coerce';
import { sectionInner, kickerStyle } from '../_shared/styles';
import { PortalLink } from '@akademi/site-builder-runtime';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

export interface HeroEditorialProps {
  kicker?: Localized;
  title?: Localized;
  subtitle?: Localized;
  ctaLabel?: Localized;
  ctaHref?: string;
  align?: 'left' | 'center';
  topRule?: boolean;
  bottomRule?: boolean;
  background?: 'paper' | 'tinted' | 'plain';
  size?: 'display' | 'compact';
  /** 'full' (default) lets content span the section edge-to-edge;
   *  'theme' constrains it to the page's max-width container. */
  maxWidth?: 'full' | 'theme';
}

export function HeroEditorial({
  kicker,
  title,
  subtitle,
  ctaLabel,
  ctaHref,
  align = 'left',
  topRule = false,
  bottomRule = true,
  background = 'paper',
  size = 'display',
  maxWidth = 'full',
}: HeroEditorialProps) {
  const { t } = useTranslation('site-builder-blocks');
  const isCompact = size === 'compact';
  const bg =
    background === 'tinted'
      ? 'var(--color-brand-50, #f0f4ff)'
      : background === 'plain'
        ? 'var(--color-surface-page, #f7f8fa)'
        : 'var(--color-surface-card, #fff)';
  const ruleStyle: CSSProperties = {
    height: 1,
    background: 'var(--color-text-primary, #0f1218)',
    opacity: 0.85,
    margin: 0,
  };

  // In compact mode the bottom rule defaults off — feed headers
  // don't need a divider before the chips/spotlight that follow.
  // Authors can force it on by passing {@code bottomRule={true}}.
  const showBottomRule = isCompact ? bottomRule === true : bottomRule;
  const showTopRule = isCompact ? topRule === true : topRule;

  const headingClamp = isCompact ? 'clamp(28px, 3.6vw, 40px)' : 'clamp(40px, 7vw, 96px)';
  const headingWeight = isCompact ? 800 : 500;
  const headingLineHeight = isCompact ? 1.15 : 1.02;

  const subtitleClamp = isCompact ? 'clamp(14px, 1.1vw, 16px)' : 'clamp(18px, 1.6vw, 22px)';
  const subtitleLineHeight = isCompact ? 1.4 : 1.5;

  const padBlock = isCompact ? 'var(--space-10, 40px)' : 'var(--space-16, 64px)';
  const padInline = 'var(--space-6, 24px)';
  const ruleGap = isCompact ? 'var(--space-6, 24px)' : 'var(--space-10, 40px)';

  return (
    <BlockRoot
      as="section"
      style={{
        padding: `${padBlock} ${padInline}`,
        background: bg,
        ...(maxWidth === 'theme'
          ? {
              maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
              width: '100%',
              margin: '0 auto',
            }
          : {}),
      }}
    >
      <div style={{ ...sectionInner, maxWidth: 'none', textAlign: align }}>
        {showTopRule && <div style={{ ...ruleStyle, marginBottom: ruleGap }} />}
        {kicker && (
          <div
            style={{
              ...kickerStyle,
              marginBottom: isCompact ? 8 : 'var(--space-6, 24px)',
              fontSize: 'var(--font-size-xs, 12px)',
            }}
          >
            {tx(kicker)}
          </div>
        )}
        <h1
          style={{
            margin: 0,
            fontSize: headingClamp,
            lineHeight: headingLineHeight,
            fontWeight: headingWeight,
            letterSpacing: 0,
            fontFamily: 'var(--font-family-display, inherit)',
            color: 'var(--color-text-primary, #0f1218)',
          }}
        >
          {tx(title) || t('hero.editorial.title')}
        </h1>
        {subtitle && (
          <p
            style={{
              marginTop: isCompact ? 8 : 'var(--space-6, 24px)',
              fontSize: subtitleClamp,
              lineHeight: subtitleLineHeight,
              maxWidth: align === 'center' ? 720 : 640,
              margin:
                align === 'center'
                  ? `${isCompact ? 8 : 24}px auto 0`
                  : `${isCompact ? 8 : 24}px 0 0`,
              color: 'var(--color-text-secondary, #5f6675)',
            }}
          >
            {tx(subtitle)}
          </p>
        )}
        {ctaLabel && (
          <div
            style={{
              marginTop: isCompact ? 16 : 'var(--space-8, 32px)',
              display: align === 'center' ? 'flex' : 'block',
              justifyContent: align === 'center' ? 'center' : 'flex-start',
            }}
          >
            <PortalLink
              href={ctaHref ?? '#'}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 'var(--space-2, 8px)',
                padding: isCompact ? '10px 20px' : '14px 28px',
                border: '1px solid var(--color-text-primary, #0f1218)',
                borderRadius: 'var(--radius-pill, 9999px)',
                color: 'var(--color-text-primary, #0f1218)',
                textDecoration: 'none',
                fontSize: isCompact ? 14 : 'var(--font-size-md, 16px)',
                fontWeight: 'var(--font-weight-medium, 500)',
                fontFamily: 'var(--font-family-body, inherit)',
                letterSpacing: 0,
              }}
            >
              {tx(ctaLabel)} <span aria-hidden>→</span>
            </PortalLink>
          </div>
        )}
        {showBottomRule && <div style={{ ...ruleStyle, marginTop: ruleGap }} />}
      </div>
    </BlockRoot>
  );
}
